// features/workstations/catalyst/open-predictions-section.js
// Open Predictions section. Empty state until Phase 8 of the
// build (when predictions start getting locked). Reads from
// catalyst_predictions where outcome_locked_at IS NULL.

import { useEffect, useState } from 'react';

export default function OpenPredictionsSection({ userId }) {
  const [data, setData] = useState({ predictions: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/catalyst/list-predictions${qs}&open=1`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const payload = await res.json();
        if (cancelled) return;
        setData({ predictions: payload.predictions || [], loading: false, error: null });
      } catch (err) {
        if (!cancelled) setData((s) => ({ ...s, loading: false, error: err.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (data.loading) {
    return <div className="cat-section"><div className="cat-loading">Loading predictions…</div></div>;
  }
  if (data.error) {
    return (
      <div className="cat-section">
        <div className="cat-error">
          Could not load predictions: {data.error}
        </div>
      </div>
    );
  }

  return (
    <div className="cat-section">
      <div className="cat-block">
        <div className="cat-block-title">Currently locked, awaiting D+30 outcome</div>
        <div className="cat-block-subtitle">
          Predictions locked under the active methodology version. Each row shows the
          locked stimulus card, predicted sustain probability, and countdown to the
          D+30 horizon. Outcomes auto-score from TradingView closing prices when D+30 hits.
        </div>
        {data.predictions.length === 0 ? (
          <div className="cat-empty">
            <div style={{ marginBottom: 8, color: '#c8a96e' }}>No open predictions yet.</div>
            <div>
              Predictions become available once the build reaches <strong>Phase 8</strong> (pre-registered
              prediction pipeline). Until then, the universe filter (Phase 2), corpus build (Phase 3),
              and pump classifier (Phase 6) need to ship first.
            </div>
            <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
              See <strong>docs/PENNY_CATALYST_SUSTAIN_BUILD.md</strong> for the build sequence.
            </div>
          </div>
        ) : (
          <div className="cat-hypothesis-list">
            {data.predictions.map((p) => <PredictionRow key={p.id} prediction={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PredictionRow({ prediction }) {
  const ticker = prediction.ticker;
  const eventTs = prediction.event_timestamp;
  const lockTs = prediction.prediction_locked_at;
  const probSustain = prediction.predicted_outcome?.d30_sustain_prob;
  return (
    <div className="cat-hyp-row">
      <div className="cat-hyp-row-left">
        <div className="cat-hyp-code">
          {ticker} · {prediction.event_class}
        </div>
        <div className="cat-hyp-meta">
          <span className="cat-hyp-version">locked {formatDate(lockTs)}</span>
          <span className="cat-hyp-vertical">event {formatDate(eventTs)}</span>
          {typeof probSustain === 'number' && (
            <span className="cat-hyp-version">P(sustain) = {(probSustain * 100).toFixed(0)}%</span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toISOString().slice(0, 10); }
  catch { return ts; }
}
